import React, { useState, useEffect, useRef, useCallback } from 'react';
import FilterNav from './FilterNav';
import WorkspaceList from './WorkspaceList';
import SidePanel from '../ui/SidePanel';
import MultiSelect from '../ui/MultiSelect';
import StylesMenu from '../ui/StylesMenu';
import typography from '../../utils/typography';
import MediumButton from '../ui/MediumButton';
import colors from '../../utils/colors';

// Plus Icon for Audio button
const PlusIcon = () => (
  <svg width={16} height={16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 2C7.52661 2 7.14286 2.38376 7.14286 2.85714V7.14286L2.85714 7.14286C2.38376 7.14286 2 7.52661 2 8C2 8.47339 2.38376 8.85714 2.85714 8.85714H7.14286V13.1429C7.14286 13.6162 7.52661 14 8 14C8.47339 14 8.85714 13.6162 8.85714 13.1429V8.85714H13.1429C13.6162 8.85714 14 8.47339 14 8C14 7.52661 13.6162 7.14286 13.1429 7.14286L8.85714 7.14286V2.85714C8.85714 2.38376 8.47339 2 8 2Z" fill="#FAF7F5"/>
  </svg>
);

const LibraryTab = ({ label, isActive, onClick }) => (
  <div 
    className={`flex justify-center items-center h-10 px-4 cursor-pointer transition-colors relative ${
      isActive ? 'text-[#faf7f5]' : 'text-[#726e6c] hover:text-[#faf7f5]'
    }`}
    onClick={onClick}
  >
    <p className="text-sm font-medium">{label}</p>
    {isActive && (
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />
    )}
  </div>
);

const Library = ({ hasPlaybar = false, onPlaySong, onPauseSong, currentlyPlayingSong, onSidePanelStateChange }) => {
  const [activeTab, setActiveTab] = useState('songs');
  const [activeFilters, setActiveFilters] = useState(['hide-disliked', 'hide-stems']);
  const [searchValue, setSearchValue] = useState('');
  const [filterCount, setFilterCount] = useState(2);
  const [currentSort, setCurrentSort] = useState('newest');
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [currentView, setCurrentView] = useState('list');
  const [likedSongs, setLikedSongs] = useState(new Set());
  const [dislikedSongs, setDislikedSongs] = useState(new Set());
  const [commentedSongs, setCommentedSongs] = useState(new Set());
  const [publishedSongs, setPublishedSongs] = useState(new Set());
  const [selectedSongs, setSelectedSongs] = useState(new Set());
  const [deletedSongs, setDeletedSongs] = useState(new Set());
  const [playedSongs, setPlayedSongs] = useState(new Set());
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [sidePanelSong, setSidePanelSong] = useState(null);
  const [scrollOpacity, setScrollOpacity] = useState(0);
  const contentRef = useRef(null);
  const [showToast, setShowToast] = useState(false);
  const [deletedSongsBackup, setDeletedSongsBackup] = useState(new Set());
  const [songLikeCounts, setSongLikeCounts] = useState(new Map());
  
  // Lazy loading state
  const [loadedSongsCount, setLoadedSongsCount] = useState(30);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreSongs, setHasMoreSongs] = useState(true);
  
  // Song editing state
  const [songTitles, setSongTitles] = useState(new Map());

  const tabs = [
    { id: 'songs', label: 'Songs' },
    { id: 'playlists', label: 'Playlists' },
    { id: 'workspaces', label: 'Workspaces' },
    { id: 'personas', label: 'Personas' },
    { id: 'hooks', label: 'Hooks' },
    { id: 'history', label: 'History' },
    { id: 'archive', label: 'Archive' }
  ];

  const handleFilterClick = (filterId) => {
    setActiveFilters(prev => {
      const isActive = prev.includes(filterId);
      let newFilters;
      
      if (isActive) {
        newFilters = prev.filter(id => id !== filterId);
      } else {
        newFilters = [...prev, filterId];
      }
      
      // Update filter count (excluding 'filters' button itself)
      setFilterCount(newFilters.filter(id => id !== 'filters').length);
      console.log('Filter clicked:', filterId);
      return newFilters;
    });
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    console.log('Search value:', value);
  };

  const handleSortChange = (sortId) => {
    setCurrentSort(sortId);
    console.log('Sort changed:', sortId);
  };

  const handleStyleToggle = (style) => {
    setSelectedStyles(prev => {
      const isSelected = prev.includes(style);
      if (isSelected) {
        return prev.filter(s => s !== style);
      } else {
        return [...prev, style];
      }
    });
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const handleTitleEdit = (songId, newTitle) => {
    setSongTitles(prev => {
      const newTitles = new Map(prev);
      newTitles.set(songId, newTitle);
      return newTitles;
    });
    console.log(`Updated song ${songId} title to: ${newTitle}`);
  };

  // Extract available styles from song data
  const getAvailableStyles = () => {
    const librarySongs = getLibrarySongs();
    const allStyles = new Set();
    
    librarySongs.forEach(song => {
      if (song.genres) {
        // Split genres by comma and trim whitespace
        const genres = song.genres.split(',').map(genre => genre.trim());
        genres.forEach(genre => allStyles.add(genre));
      }
    });
    
    return Array.from(allStyles).sort();
  };

  // Calculate style counts
  const getStyleCounts = () => {
    const librarySongs = getLibrarySongs();
    const styleCounts = {};
    
    librarySongs.forEach(song => {
      if (song.genres) {
        // Split genres by comma and trim whitespace
        const genres = song.genres.split(',').map(genre => genre.trim());
        genres.forEach(genre => {
          styleCounts[genre] = (styleCounts[genre] || 0) + 1;
        });
      }
    });
    
    return styleCounts;
  };

  const handleCellAction = (action, songId) => {
    console.log(`${action} action for song ${songId}`);
    
    switch (action) {
      case 'like':
        const wasLiked = likedSongs.has(songId);
        setLikedSongs(prev => {
          const newSet = new Set(prev);
          if (wasLiked) {
            newSet.delete(songId);
          } else {
            newSet.add(songId);
          }
          return newSet;
        });
        
        // Update like count
        setSongLikeCounts(prev => {
          const newCounts = new Map(prev);
          const currentAdjustment = newCounts.get(songId) || 0;
          if (wasLiked) {
            newCounts.set(songId, currentAdjustment - 1);
          } else {
            newCounts.set(songId, currentAdjustment + 1);
          }
          return newCounts;
        });
        break;
      case 'dislike':
        const wasDisliked = dislikedSongs.has(songId);
        setDislikedSongs(prev => {
          const newDisliked = new Set(prev);
          if (wasDisliked) {
            newDisliked.delete(songId);
          } else {
            newDisliked.add(songId);
            // Remove from liked if it was liked
            setLikedSongs(prevLiked => {
              const newLiked = new Set(prevLiked);
              newLiked.delete(songId);
              return newLiked;
            });
          }
          return newDisliked;
        });
        console.log(`${wasDisliked ? 'Undisliked' : 'Disliked'} song ${songId}`);
        break;
      case 'comment':
        const wasCommented = commentedSongs.has(songId);
        setCommentedSongs(prev => {
          const newSet = new Set(prev);
          if (wasCommented) {
            newSet.delete(songId);
          } else {
            newSet.add(songId);
          }
          return newSet;
        });
        console.log(`${wasCommented ? 'Uncommented' : 'Commented'} song ${songId}`);
        break;
      case 'publish':
        setPublishedSongs(prev => {
          const newSet = new Set(prev);
          if (newSet.has(songId)) {
            newSet.delete(songId);
          } else {
            newSet.add(songId);
          }
          return newSet;
        });
        break;
      case 'select':
        setSelectedSongs(prev => {
          const newSet = new Set(prev);
          if (newSet.has(songId)) {
            newSet.delete(songId);
          } else {
            newSet.add(songId);
          }
          return newSet;
        });
        break;
      default:
        break;
    }
  };

  // MultiSelect handlers
  const handleMultiSelectClear = () => {
    setSelectedSongs(new Set());
  };

  const handleMultiSelectDownload = () => {
    console.log('Downloading selected songs:', Array.from(selectedSongs));
  };

  const handleMultiSelectNewWorkspace = () => {
    console.log('Creating new workspace with selected songs:', Array.from(selectedSongs));
  };

  const handleMultiSelectAddToPlaylist = () => {
    console.log('Adding selected songs to playlist:', Array.from(selectedSongs));
  };

  const handleMultiSelectDelete = () => {
    const selectedSongIds = Array.from(selectedSongs);
    console.log('Deleting selected songs:', selectedSongIds);
    
    setDeletedSongsBackup(new Set(selectedSongIds));
    
    setDeletedSongs(prev => {
      const newDeleted = new Set(prev);
      selectedSongIds.forEach(id => newDeleted.add(id));
      return newDeleted;
    });
    
    setSelectedSongs(new Set());
    setShowToast(true);
  };

  const handleUndo = () => {
    setDeletedSongs(prev => {
      const newDeleted = new Set(prev);
      deletedSongsBackup.forEach(id => newDeleted.delete(id));
      return newDeleted;
    });
    
    setDeletedSongsBackup(new Set());
    setShowToast(false);
  };

  const handleToastDismiss = () => {
    setShowToast(false);
    setDeletedSongsBackup(new Set());
  };

  // Helper function to get current like count for a song
  const getCurrentLikeCount = (songId) => {
    const librarySongs = getLibrarySongs();
    const song = librarySongs.find(s => s.id === songId);
    const originalCount = song?.likeCount || 0;
    const adjustment = songLikeCounts.get(songId) || 0;
    return originalCount + adjustment;
  };

  // Get library songs data with updated like counts and titles (limited by loaded count)
  const getLibrarySongsWithCounts = () => {
    const baseSongs = getLibrarySongs();
    const limitedSongs = baseSongs.slice(0, loadedSongsCount);
    return limitedSongs.map(song => ({
      ...song,
      title: songTitles.get(song.id) || song.title,
      likeCount: getCurrentLikeCount(song.id)
    }));
  };

  // Load more songs function
  const loadMoreSongs = useCallback(() => {
    if (isLoadingMore || !hasMoreSongs) return;
    
    setIsLoadingMore(true);
    
    // Simulate network delay
    setTimeout(() => {
      const allSongs = getLibrarySongs();
      const newCount = Math.min(loadedSongsCount + 30, allSongs.length);
      
      setLoadedSongsCount(newCount);
      setIsLoadingMore(false);
      
      // Check if we've loaded all songs
      if (newCount >= allSongs.length) {
        setHasMoreSongs(false);
      }
    }, 1500); // 1.5 second loading delay
  }, [isLoadingMore, hasMoreSongs, loadedSongsCount]);

  const handleSidePanelLike = () => {
    if (sidePanelSong) {
      const wasLiked = likedSongs.has(sidePanelSong.id);
      const countChange = wasLiked ? -1 : 1;
      
      setLikedSongs(prev => {
        const newLiked = new Set(prev);
        if (wasLiked) {
          newLiked.delete(sidePanelSong.id);
        } else {
          newLiked.add(sidePanelSong.id);
        }
        return newLiked;
      });

      // Update like count
      setSongLikeCounts(prev => {
        const newCounts = new Map(prev);
        const currentAdjustment = newCounts.get(sidePanelSong.id) || 0;
        newCounts.set(sidePanelSong.id, currentAdjustment + countChange);
        return newCounts;
      });

      // Update the sidePanelSong object to reflect new count
      setSidePanelSong(prev => ({
        ...prev,
        likeCount: prev.likeCount + countChange
      }));
    }
  };

  const handleAudioClick = () => {
    console.log('Audio button clicked');
  };

  const handleSidePanelClose = () => {
    setSidePanelOpen(false);
    setSidePanelSong(null);
    onSidePanelStateChange?.(false, 'library');
  };

  // Get library songs data
  const getLibrarySongs = () => {
    return [
      {
        id: 1,
        title: "Blue Light Goodbye",
        genres: "Electronic, Techno, House",
        duration: "2:56",
        image: "https://images.unsplash.com/photo-1611348586804-61bf6c080437?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1200,
        commentCount: 230
      },
      {
        id: 2,
        title: "Lost in Translation (remix)",
        genres: "Folk, Ambient, Electronic",
        duration: "3:42",
        image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=67&h=67&fit=crop&crop=center",
        status: "inactive",
        isNew: false,
        likeCount: 890,
        commentCount: 156
      },
      {
        id: 3,
        title: "Electric Pulse",
        genres: "Electronic, House, EDM",
        duration: "4:18",
        image: "https://images.unsplash.com/photo-1571330735066-03aaa9429d89?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2100,
        commentCount: 342
      },
      {
        id: 4,
        title: "After Hours",
        genres: "Jazz, Soul, Ambient",
        duration: "5:23",
        image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
        status: "pending",
        isNew: false,
        likeCount: 567,
        commentCount: 89
      },
      {
        id: 5,
        title: "Untitled",
        genres: "Ambient, Chillout, Folk",
        duration: "6:12",
        image: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3400,
        commentCount: 467
      },
      {
        id: 6,
        title: "Neon Nights",
        genres: "Electronic, Synthwave, Chillout",
        duration: "3:57",
        image: "https://images.unsplash.com/photo-1619983081563-430f63602796?w=67&h=67&fit=crop&crop=center",
        status: "inactive",
        isNew: false,
        likeCount: 1890,
        commentCount: 278
      },
      {
        id: 7,
        title: "Campfire Stories",
        genres: "Folk, Acoustic, Ambient",
        duration: "4:31",
        image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 745,
        commentCount: 123
      },
      {
        id: 8,
        title: "Binary Dreams (remix)",
        genres: "Electronic, Bass, EDM",
        duration: "3:14",
        image: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=67&h=67&fit=crop&crop=center",
        status: "pending",
        isNew: false,
        likeCount: 1567,
        commentCount: 234
      },
      {
        id: 9,
        title: "Interstellar",
        genres: "Rock, Psychedelic, Electronic",
        duration: "7:45",
        image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 4200,
        commentCount: 445
      },
      {
        id: 10,
        title: "City Lights",
        genres: "Hip Hop, Soul, Jazz",
        duration: "3:28",
        image: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
        status: "inactive",
        isNew: false,
        likeCount: 923,
        commentCount: 167
      },
      {
        id: 11,
        title: "Untitled",
        genres: "Folk, Country, Rock",
        duration: "4:02",
        image: "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 678,
        commentCount: 98
      },
      {
        id: 12,
        title: "Frequency Shift (remix)",
        genres: "Electronic, Bass, Experimental",
        duration: "5:17",
        image: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=67&h=67&fit=crop&crop=center",
        status: "pending",
        isNew: false,
        likeCount: 1345,
        commentCount: 201
      },
      {
        id: 13,
        title: "Solar Flare",
        genres: "Rock, Alternative, Electronic",
        duration: "3:36",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2890,
        commentCount: 389
      },
      {
        id: 14,
        title: "Deep Waters",
        genres: "Ambient, Meditation, Chillout",
        duration: "8:21",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "inactive",
        isNew: false,
        likeCount: 512,
        commentCount: 67
      },
      {
        id: 15,
        title: "Nostalgic",
        genres: "Chillout, Electronic, Jazz",
        duration: "4:44",
        image: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1789,
        commentCount: 289
      },
      {
        id: 16,
        title: "Midnight Reflections",
        genres: "Electronic, Ambient, Chillout",
        duration: "5:12",
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2340,
        commentCount: 156
      },
      {
        id: 17,
        title: "Morning Coffee",
        genres: "Jazz, Chillout, Soul",
        duration: "3:24",
        image: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 987,
        commentCount: 78
      },
      {
        id: 18,
        title: "Lighthouse (remix)",
        genres: "Electronic, EDM, Bass",
        duration: "4:07",
        image: "https://images.unsplash.com/photo-1571330735066-03aaa9429d89?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3245,
        commentCount: 234
      },
      {
        id: 19,
        title: "Whispering Pines",
        genres: "Folk, Acoustic, Ambient",
        duration: "6:33",
        image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1456,
        commentCount: 189
      },
      {
        id: 20,
        title: "Thunder Road (remix)",
        genres: "Bass, Electronic, EDM",
        duration: "3:45",
        image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 4321,
        commentCount: 567
      },
      {
        id: 21,
        title: "Golden Hour",
        genres: "Ambient, Meditation, Chillout",
        duration: "8:12",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "inactive",
        isNew: false,
        likeCount: 678,
        commentCount: 45
      },
      {
        id: 22,
        title: "Untitled",
        genres: "Jazz, Soul, Chillout",
        duration: "4:56",
        image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1876,
        commentCount: 234
      },
      {
        id: 23,
        title: "Breaking Point",
        genres: "Rock, Alternative, Electronic",
        duration: "3:28",
        image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 5432,
        commentCount: 678
      },
      {
        id: 24,
        title: "Stellar Drift (remix)",
        genres: "Electronic, Psychedelic, Ambient",
        duration: "7:21",
        image: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2987,
        commentCount: 345
      },
      {
        id: 25,
        title: "Homeward Bound",
        genres: "Folk, Country, Acoustic",
        duration: "4:12",
        image: "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1234,
        commentCount: 123
      },
      {
        id: 26,
        title: "Signal Lost",
        genres: "Experimental, Electronic, Bass",
        duration: "6:45",
        image: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=67&h=67&fit=crop&crop=center",
        status: "pending",
        isNew: false,
        likeCount: 876,
        commentCount: 98
      },
      {
        id: 27,
        title: "Phoenix Rising",
        genres: "Soul, Jazz, Chillout",
        duration: "5:34",
        image: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2345,
        commentCount: 287
      },
      {
        id: 28,
        title: "Void Walker (remix)",
        genres: "Ambient, Electronic, Meditation",
        duration: "9:12",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1567,
        commentCount: 156
      },
      {
        id: 29,
        title: "Street Symphony",
        genres: "Hip Hop, Jazz, Soul",
        duration: "3:56",
        image: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3456,
        commentCount: 445
      },
      {
        id: 30,
        title: "Untitled",
        genres: "Folk, Electronic, Ambient",
        duration: "4:23",
        image: "https://images.unsplash.com/photo-1611348586804-61bf6c080437?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1987,
        commentCount: 198
      },
      {
        id: 31,
        title: "Earthquake (remix)",
        genres: "Bass, Electronic, EDM",
        duration: "4:45",
        image: "https://images.unsplash.com/photo-1619983081563-430f63602796?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 4567,
        commentCount: 567
      },
      {
        id: 32,
        title: "Kaleidoscope Dreams",
        genres: "Psychedelic, Rock, Electronic",
        duration: "8:34",
        image: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2876,
        commentCount: 334
      },
      {
        id: 33,
        title: "Lazy Sunday",
        genres: "Chillout, Ambient, Jazz",
        duration: "5:21",
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1765,
        commentCount: 234
      },
      {
        id: 34,
        title: "Voltage (remix)",
        genres: "Rock, Electronic, Alternative",
        duration: "3:67",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3234,
        commentCount: 456
      },
      {
        id: 35,
        title: "Blue Monday",
        genres: "Jazz, Electronic, Soul",
        duration: "6:12",
        image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2134,
        commentCount: 289
      },
      {
        id: 36,
        title: "Paper Hearts",
        genres: "Acoustic, Folk, Ambient",
        duration: "4:45",
        image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1456,
        commentCount: 167
      },
      {
        id: 37,
        title: "Supersonic (remix)",
        genres: "EDM, Electronic, Bass",
        duration: "3:23",
        image: "https://images.unsplash.com/photo-1571330735066-03aaa9429d89?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 5678,
        commentCount: 678
      },
      {
        id: 38,
        title: "Inner Peace",
        genres: "Meditation, Ambient, Chillout",
        duration: "10:45",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 987,
        commentCount: 87
      },
      {
        id: 39,
        title: "Untitled",
        genres: "Country, Electronic, Folk",
        duration: "4:12",
        image: "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1678,
        commentCount: 198
      },
      {
        id: 40,
        title: "Neon Soul (remix)",
        genres: "Soul, Electronic, Jazz",
        duration: "5:34",
        image: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2456,
        commentCount: 334
      },
      {
        id: 41,
        title: "Silent Thunder",
        genres: "Bass, Ambient, Electronic",
        duration: "7:23",
        image: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1234,
        commentCount: 145
      },
      {
        id: 42,
        title: "Wildfire",
        genres: "Folk, Rock, Acoustic",
        duration: "4:56",
        image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2876,
        commentCount: 267
      },
      {
        id: 43,
        title: "Dreamscape (remix)",
        genres: "Electronic, Chillout, Ambient",
        duration: "6:12",
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1987,
        commentCount: 234
      },
      {
        id: 44,
        title: "Midnight Blues",
        genres: "Jazz, Soul, Chillout",
        duration: "5:45",
        image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2345,
        commentCount: 298
      },
      {
        id: 45,
        title: "Untitled",
        genres: "Experimental, Bass, Electronic",
        duration: "5:23",
        image: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1678,
        commentCount: 189
      },
      {
        id: 46,
        title: "Neon Ghosts (remix)",
        genres: "Alternative, Electronic, Rock",
        duration: "4:12",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3456,
        commentCount: 445
      },
      {
        id: 47,
        title: "Moonlit Serenade",
        genres: "Ambient, Jazz, Meditation",
        duration: "8:34",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1234,
        commentCount: 156
      },
      {
        id: 48,
        title: "Digital Streets (remix)",
        genres: "Electronic, Hip Hop, Bass",
        duration: "3:45",
        image: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 4321,
        commentCount: 567
      },
      {
        id: 49,
        title: "Crystal Visions",
        genres: "Psychedelic, Ambient, Electronic",
        duration: "9:12",
        image: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2567,
        commentCount: 334
      },
      {
        id: 50,
        title: "Retro Future (remix)",
        genres: "Synthwave, Electronic, Chillout",
        duration: "5:23",
        image: "https://images.unsplash.com/photo-1619983081563-430f63602796?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3456,
        commentCount: 445
      },
      {
        id: 51,
        title: "Untitled",
        genres: "Folk, Meditation, Ambient",
        duration: "7:56",
        image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1456,
        commentCount: 178
      },
      {
        id: 52,
        title: "Groove Machine (remix)",
        genres: "Soul, Bass, Electronic",
        duration: "4:34",
        image: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2876,
        commentCount: 334
      },
      {
        id: 53,
        title: "Echo Chamber",
        genres: "Rock, Ambient, Psychedelic",
        duration: "6:45",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2234,
        commentCount: 267
      },
      {
        id: 54,
        title: "Blue Algorithm (remix)",
        genres: "Jazz, Electronic, Chillout",
        duration: "5:12",
        image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1987,
        commentCount: 234
      },
      {
        id: 55,
        title: "Unplugged Wires",
        genres: "Acoustic, Electronic, Folk",
        duration: "4:23",
        image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1678,
        commentCount: 189
      },
      {
        id: 56,
        title: "Sunrise Beach (remix)",
        genres: "EDM, Chillout, Electronic",
        duration: "4:56",
        image: "https://images.unsplash.com/photo-1571330735066-03aaa9429d89?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3456,
        commentCount: 445
      },
      {
        id: 57,
        title: "Broken Dreams",
        genres: "Alternative, Soul, Jazz",
        duration: "5:34",
        image: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2345,
        commentCount: 298
      },
      {
        id: 58,
        title: "Deep Thoughts (remix)",
        genres: "Bass, Chillout, Electronic",
        duration: "5:45",
        image: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1987,
        commentCount: 234
      },
      {
        id: 59,
        title: "Untitled",
        genres: "Folk, Electronic, Ambient",
        duration: "6:12",
        image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1567,
        commentCount: 178
      },
      {
        id: 60,
        title: "Corner Store Anthem",
        genres: "Hip Hop, Soul, Jazz",
        duration: "4:23",
        image: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3234,
        commentCount: 445
      },
      {
        id: 61,
        title: "Digital Breathe",
        genres: "Meditation, Electronic, Ambient",
        duration: "8:45",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1234,
        commentCount: 134
      },
      {
        id: 62,
        title: "Dusty Roads",
        genres: "Country, Rock, Folk",
        duration: "4:12",
        image: "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2876,
        commentCount: 334
      },
      {
        id: 63,
        title: "Laboratory Dreams",
        genres: "Experimental, Chillout, Electronic",
        duration: "7:23",
        image: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1567,
        commentCount: 189
      },
      {
        id: 64,
        title: "Matrix Meditation",
        genres: "Techno, Ambient, Electronic",
        duration: "6:34",
        image: "https://images.unsplash.com/photo-1611348586804-61bf6c080437?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2456,
        commentCount: 298
      },
      {
        id: 65,
        title: "Sunset Lounge",
        genres: "House, Chillout, Electronic",
        duration: "5:45",
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2134,
        commentCount: 256
      },
      {
        id: 66,
        title: "Ocean Rhythm",
        genres: "Electronic, Bass, Chillout",
        duration: "4:23",
        image: "https://images.unsplash.com/photo-1571330735066-03aaa9429d89?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1987,
        commentCount: 234
      },
      {
        id: 67,
        title: "Velvet Voice",
        genres: "Jazz, Soul, Acoustic",
        duration: "5:12",
        image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2567,
        commentCount: 298
      },
      {
        id: 68,
        title: "Neon Downtown",
        genres: "Synthwave, Electronic, Chillout",
        duration: "6:34",
        image: "https://images.unsplash.com/photo-1619983081563-430f63602796?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3456,
        commentCount: 445
      },
      {
        id: 69,
        title: "Grandfather's Stories",
        genres: "Folk, Acoustic, Ambient",
        duration: "7:23",
        image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1678,
        commentCount: 189
      },
      {
        id: 70,
        title: "Subway Frequency",
        genres: "Bass, Electronic, Experimental",
        duration: "5:45",
        image: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2876,
        commentCount: 334
      },
      {
        id: 71,
        title: "Zen Garden",
        genres: "Meditation, Ambient, Folk",
        duration: "9:12",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1234,
        commentCount: 134
      },
      {
        id: 72,
        title: "Hip Hop Electronic",
        genres: "Hip Hop, Electronic, Bass",
        duration: "3:56",
        image: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 4321,
        commentCount: 567
      },
      {
        id: 73,
        title: "Front Porch",
        genres: "Country, Acoustic, Folk",
        duration: "4:34",
        image: "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1567,
        commentCount: 178
      },
      {
        id: 74,
        title: "Rebel Saxophone",
        genres: "Alternative, Jazz, Electronic",
        duration: "6:12",
        image: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2345,
        commentCount: 267
      },
      {
        id: 75,
        title: "Electric Sleep",
        genres: "Techno, Electronic, Ambient",
        duration: "7:45",
        image: "https://images.unsplash.com/photo-1611348586804-61bf6c080437?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3789,
        commentCount: 456
      },
      {
        id: 76,
        title: "Smooth Operator",
        genres: "Soul, Chillout, Jazz",
        duration: "5:23",
        image: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2134,
        commentCount: 298
      },
      {
        id: 77,
        title: "Chaos Theory",
        genres: "Rock, Experimental, Electronic",
        duration: "6:34",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2987,
        commentCount: 367
      },
      {
        id: 78,
        title: "Floating Circuits",
        genres: "Ambient, Electronic, Meditation",
        duration: "8:56",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1456,
        commentCount: 167
      },
      {
        id: 79,
        title: "Midnight Groove",
        genres: "Jazz, Bass, Electronic",
        duration: "4:45",
        image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2678,
        commentCount: 334
      },
      {
        id: 80,
        title: "Countryside Digital",
        genres: "Folk, Electronic, Chillout",
        duration: "5:12",
        image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1789,
        commentCount: 234
      },
      {
        id: 81,
        title: "Shockwave (remix)",
        genres: "EDM, Bass, Electronic",
        duration: "3:34",
        image: "https://images.unsplash.com/photo-1571330735066-03aaa9429d89?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 5678,
        commentCount: 678
      },
      {
        id: 82,
        title: "Purple Haze Dreams",
        genres: "Psychedelic, Soul, Electronic",
        duration: "7:23",
        image: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2456,
        commentCount: 298
      },
      {
        id: 83,
        title: "Lazy Afternoon",
        genres: "Chillout, Jazz, Ambient",
        duration: "6:45",
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1987,
        commentCount: 256
      },
      {
        id: 84,
        title: "Broken Radio",
        genres: "Alternative, Rock, Electronic",
        duration: "4:12",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3456,
        commentCount: 445
      },
      {
        id: 85,
        title: "Binary Zen (remix)",
        genres: "Electronic, Meditation, Ambient",
        duration: "10:12",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1123,
        commentCount: 123
      },
      {
        id: 86,
        title: "Club Foundation",
        genres: "House, Bass, Electronic",
        duration: "5:34",
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2789,
        commentCount: 334
      },
      {
        id: 87,
        title: "Backwoods Blues",
        genres: "Country, Soul, Folk",
        duration: "4:56",
        image: "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1678,
        commentCount: 189
      },
      {
        id: 88,
        title: "Broken Tempo",
        genres: "Experimental, Jazz, Electronic",
        duration: "8:23",
        image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2134,
        commentCount: 267
      },
      {
        id: 89,
        title: "Wooden Heart",
        genres: "Acoustic, Soul, Folk",
        duration: "5:45",
        image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1987,
        commentCount: 234
      },
      {
        id: 90,
        title: "Untitled",
        genres: "Bass, Meditation, Ambient",
        duration: "9:34",
        image: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1345,
        commentCount: 156
      },
      {
        id: 91,
        title: "Crossroads",
        genres: "Rock, Jazz, Electronic",
        duration: "6:12",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2876,
        commentCount: 345
      },
      {
        id: 92,
        title: "Cyber Streets (remix)",
        genres: "Electronic, Hip Hop, Soul",
        duration: "4:23",
        image: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3789,
        commentCount: 456
      },
      {
        id: 93,
        title: "Forest Cathedral",
        genres: "Ambient, Folk, Meditation",
        duration: "7:56",
        image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1567,
        commentCount: 189
      },
      {
        id: 94,
        title: "Digital Breeze",
        genres: "Chillout, Electronic, Bass",
        duration: "5:23",
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2234,
        commentCount: 289
      },
      {
        id: 95,
        title: "Neon Jazz (remix)",
        genres: "Jazz, Electronic, Ambient",
        duration: "6:45",
        image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2456,
        commentCount: 298
      },
      {
        id: 96,
        title: "Urban Pulse",
        genres: "Soul, Electronic, Bass",
        duration: "4:34",
        image: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2987,
        commentCount: 367
      },
      {
        id: 97,
        title: "Digital Rebellion (remix)",
        genres: "Alternative, Electronic, Chillout",
        duration: "5:12",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1789,
        commentCount: 234
      },
      {
        id: 98,
        title: "Cabin Sessions",
        genres: "Folk, Jazz, Acoustic",
        duration: "6:23",
        image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1456,
        commentCount: 178
      },
      {
        id: 99,
        title: "Deep Focus",
        genres: "Electronic, Bass, Meditation",
        duration: "8:45",
        image: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1678,
        commentCount: 189
      },
      {
        id: 100,
        title: "Time Dilation",
        genres: "Psychedelic, Electronic, Ambient",
        duration: "9:12",
        image: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2567,
        commentCount: 334
      },
      {
        id: 101,
        title: "Machine Heart",
        genres: "Techno, Bass, Electronic",
        duration: "5:34",
        image: "https://images.unsplash.com/photo-1611348586804-61bf6c080437?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3456,
        commentCount: 445
      },
      {
        id: 102,
        title: "Smoke Signals",
        genres: "Jazz, Ambient, Chillout",
        duration: "7:23",
        image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1987,
        commentCount: 256
      },
      {
        id: 103,
        title: "Digital Prairie",
        genres: "Country, Electronic, Folk",
        duration: "4:45",
        image: "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1678,
        commentCount: 189
      },
      {
        id: 104,
        title: "Underground Groove",
        genres: "Soul, Bass, Electronic",
        duration: "5:12",
        image: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2876,
        commentCount: 334
      },
      {
        id: 105,
        title: "Unknown Territory",
        genres: "Experimental, Ambient, Electronic",
        duration: "10:34",
        image: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1234,
        commentCount: 145
      },
      {
        id: 106,
        title: "Hip Hop Jazz",
        genres: "Hip Hop, Jazz, Electronic",
        duration: "4:56",
        image: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3234,
        commentCount: 445
      },
      {
        id: 107,
        title: "Acoustic Electronic",
        genres: "Acoustic, Electronic, Ambient",
        duration: "6:12",
        image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1789,
        commentCount: 234
      },
      {
        id: 108,
        title: "Chillout Bass",
        genres: "Chillout, Bass, Electronic",
        duration: "5:45",
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2456,
        commentCount: 298
      },
      {
        id: 109,
        title: "Rock Electronic Fusion",
        genres: "Rock, Electronic, Alternative",
        duration: "4:23",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3567,
        commentCount: 456
      },
      {
        id: 110,
        title: "Meditation Jazz",
        genres: "Meditation, Jazz, Ambient",
        duration: "9:23",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1345,
        commentCount: 156
      },
      {
        id: 111,
        title: "Electronic Folk Bass",
        genres: "Electronic, Folk, Bass",
        duration: "5:34",
        image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2234,
        commentCount: 289
      },
      {
        id: 112,
        title: "Soul Jazz Electronic",
        genres: "Soul, Jazz, Electronic",
        duration: "6:45",
        image: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2987,
        commentCount: 367
      },
      {
        id: 113,
        title: "Ambient Bass",
        genres: "Ambient, Bass, Electronic",
        duration: "8:12",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1567,
        commentCount: 189
      },
      {
        id: 114,
        title: "Psychedelic Jazz",
        genres: "Psychedelic, Jazz, Electronic",
        duration: "7:56",
        image: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2456,
        commentCount: 298
      },
      {
        id: 115,
        title: "Country Jazz",
        genres: "Country, Jazz, Folk",
        duration: "5:23",
        image: "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1789,
        commentCount: 234
      },
      {
        id: 116,
        title: "Electronic Experimental",
        genres: "Electronic, Experimental, Bass",
        duration: "6:34",
        image: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1678,
        commentCount: 189
      },
      {
        id: 117,
        title: "Hip Hop Electronic",
        genres: "Hip Hop, Electronic, Bass",
        duration: "4:12",
        image: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 4123,
        commentCount: 567
      },
      {
        id: 118,
        title: "Folk Chillout",
        genres: "Folk, Chillout, Ambient",
        duration: "7:45",
        image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1456,
        commentCount: 178
      },
      {
        id: 119,
        title: "Rebel Saxophone",
        genres: "Alternative, Jazz, Soul",
        duration: "5:56",
        image: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2567,
        commentCount: 334
      },
      {
        id: 120,
        title: "Electronic Chillout Bass",
        genres: "Electronic, Chillout, Bass",
        duration: "6:23",
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2876,
        commentCount: 345
      },
      {
        id: 121,
        title: "Acoustic Jazz",
        genres: "Acoustic, Jazz, Folk",
        duration: "5:12",
        image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1987,
        commentCount: 256
      },
      {
        id: 122,
        title: "Bass Electronic Ambient",
        genres: "Bass, Electronic, Ambient",
        duration: "8:34",
        image: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1678,
        commentCount: 189
      },
      {
        id: 123,
        title: "Soul Ambient",
        genres: "Soul, Ambient, Jazz",
        duration: "7:12",
        image: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2234,
        commentCount: 289
      },
      {
        id: 124,
        title: "Rock Bass",
        genres: "Rock, Bass, Electronic",
        duration: "4:45",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3456,
        commentCount: 445
      },
      {
        id: 125,
        title: "Meditation Electronic Bass",
        genres: "Meditation, Electronic, Bass",
        duration: "10:23",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1234,
        commentCount: 134
      },
      {
        id: 126,
        title: "Jazz Soul Chillout",
        genres: "Jazz, Soul, Chillout",
        duration: "6:56",
        image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2789,
        commentCount: 356
      },
      {
        id: 127,
        title: "Electronic Folk Ambient",
        genres: "Electronic, Folk, Ambient",
        duration: "7:34",
        image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1567,
        commentCount: 189
      },
      {
        id: 128,
        title: "Corner Store Anthem",
        genres: "Hip Hop, Soul, Electronic",
        duration: "4:23",
        image: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3789,
        commentCount: 467
      },
      {
        id: 129,
        title: "Chillout Jazz Ambient",
        genres: "Chillout, Jazz, Ambient",
        duration: "8:45",
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1987,
        commentCount: 234
      },
      {
        id: 130,
        title: "Alternative Electronic Bass",
        genres: "Alternative, Electronic, Bass",
        duration: "5:12",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2456,
        commentCount: 298
      },
      {
        id: 131,
        title: "Folk Electronic Jazz",
        genres: "Folk, Electronic, Jazz",
        duration: "6:34",
        image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1678,
        commentCount: 189
      },
      {
        id: 132,
        title: "Bass Soul Electronic",
        genres: "Bass, Soul, Electronic",
        duration: "4:56",
        image: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2987,
        commentCount: 367
      },
      {
        id: 133,
        title: "Ambient Electronic Jazz",
        genres: "Ambient, Electronic, Jazz",
        duration: "9:12",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1456,
        commentCount: 167
      },
      {
        id: 134,
        title: "Rock Electronic Chillout",
        genres: "Rock, Electronic, Chillout",
        duration: "5:45",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2567,
        commentCount: 334
      },
      {
        id: 135,
        title: "Country Electronic Bass",
        genres: "Country, Electronic, Bass",
        duration: "4:34",
        image: "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1789,
        commentCount: 234
      },
      {
        id: 136,
        title: "Experimental Electronic",
        genres: "Experimental, Electronic, Ambient",
        duration: "11:23",
        image: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1234,
        commentCount: 145
      },
      {
        id: 137,
        title: "Hip Hop Chillout",
        genres: "Hip Hop, Chillout, Electronic",
        duration: "4:12",
        image: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3456,
        commentCount: 445
      },
      {
        id: 138,
        title: "Jazz Electronic Bass",
        genres: "Jazz, Electronic, Bass",
        duration: "6:23",
        image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2876,
        commentCount: 356
      },
      {
        id: 139,
        title: "Folk Soul",
        genres: "Folk, Soul, Acoustic",
        duration: "5:56",
        image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1678,
        commentCount: 189
      },
      {
        id: 140,
        title: "Electronic Bass Chillout",
        genres: "Electronic, Bass, Chillout",
        duration: "7:45",
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2234,
        commentCount: 289
      },
      {
        id: 141,
        title: "Soul Electronic Ambient",
        genres: "Soul, Electronic, Ambient",
        duration: "8:12",
        image: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1987,
        commentCount: 256
      },
      {
        id: 142,
        title: "Alternative Bass",
        genres: "Alternative, Bass, Electronic",
        duration: "4:45",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2456,
        commentCount: 298
      },
      {
        id: 143,
        title: "Meditation Folk",
        genres: "Meditation, Folk, Ambient",
        duration: "9:34",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1345,
        commentCount: 156
      },
      {
        id: 144,
        title: "Jazz Bass Chillout",
        genres: "Jazz, Bass, Chillout",
        duration: "6:12",
        image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2567,
        commentCount: 334
      },
      {
        id: 145,
        title: "Electronic Acoustic",
        genres: "Electronic, Acoustic, Ambient",
        duration: "5:23",
        image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1789,
        commentCount: 234
      },
      {
        id: 146,
        title: "Hip Hop Bass Jazz",
        genres: "Hip Hop, Bass, Jazz",
        duration: "4:56",
        image: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3789,
        commentCount: 467
      },
      {
        id: 147,
        title: "Rock Ambient Electronic",
        genres: "Rock, Ambient, Electronic",
        duration: "7:23",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2234,
        commentCount: 289
      },
      {
        id: 148,
        title: "Chillout Electronic Soul",
        genres: "Chillout, Electronic, Soul",
        duration: "6:45",
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1987,
        commentCount: 256
      },
      {
        id: 149,
        title: "Folk Bass Electronic",
        genres: "Folk, Bass, Electronic",
        duration: "5:34",
        image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1678,
        commentCount: 189
      },
      {
        id: 150,
        title: "Jazz Soul Bass",
        genres: "Jazz, Soul, Bass",
        duration: "6:56",
        image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2876,
        commentCount: 356
      },
      {
        id: 151,
        title: "Ambient Electronic Soul",
        genres: "Ambient, Electronic, Soul",
        duration: "8:23",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1456,
        commentCount: 167
      },
      {
        id: 152,
        title: "Country Bass Electronic",
        genres: "Country, Bass, Electronic",
        duration: "4:12",
        image: "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1789,
        commentCount: 234
      },
      {
        id: 153,
        title: "Experimental Soul",
        genres: "Experimental, Soul, Electronic",
        duration: "9:12",
        image: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1234,
        commentCount: 145
      },
      {
        id: 154,
        title: "Electronic Jazz Chillout",
        genres: "Electronic, Jazz, Chillout",
        duration: "7:34",
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2567,
        commentCount: 334
      },
      {
        id: 155,
        title: "Hip Hop Electronic Soul",
        genres: "Hip Hop, Electronic, Soul",
        duration: "4:45",
        image: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3456,
        commentCount: 445
      },
      {
        id: 156,
        title: "Folk Electronic Soul",
        genres: "Folk, Electronic, Soul",
        duration: "6:23",
        image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1987,
        commentCount: 256
      },
      {
        id: 157,
        title: "Bass Chillout Jazz",
        genres: "Bass, Chillout, Jazz",
        duration: "5:56",
        image: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2234,
        commentCount: 289
      },
      {
        id: 158,
        title: "Alternative Soul Electronic",
        genres: "Alternative, Soul, Electronic",
        duration: "5:12",
        image: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2456,
        commentCount: 298
      },
      {
        id: 159,
        title: "Meditation Electronic Jazz",
        genres: "Meditation, Electronic, Jazz",
        duration: "10:45",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1345,
        commentCount: 156
      },
      {
        id: 160,
        title: "Electronic Folk Bass",
        genres: "Electronic, Folk, Bass",
        duration: "6:34",
        image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1678,
        commentCount: 189
      },
      {
        id: 161,
        title: "Jazz Electronic Soul",
        genres: "Jazz, Electronic, Soul",
        duration: "7:12",
        image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2789,
        commentCount: 356
      },
      {
        id: 162,
        title: "Ambient Bass Jazz",
        genres: "Ambient, Bass, Jazz",
        duration: "8:45",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1567,
        commentCount: 189
      },
      {
        id: 163,
        title: "Rock Electronic Soul",
        genres: "Rock, Electronic, Soul",
        duration: "4:23",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3234,
        commentCount: 445
      },
      {
        id: 164,
        title: "Chillout Bass Soul",
        genres: "Chillout, Bass, Soul",
        duration: "6:12",
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1987,
        commentCount: 256
      },
      {
        id: 165,
        title: "Electronic Soul Jazz",
        genres: "Electronic, Soul, Jazz",
        duration: "5:45",
        image: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2876,
        commentCount: 356
      }
    ];
  };

  const handleRowClick = (songId) => {
    console.log(`Row clicked for song ${songId} in Library`);
    const librarySongs = getLibrarySongsWithCounts();
    const songToShow = librarySongs.find(song => song.id === songId);
    if (songToShow) {
      setSidePanelSong(songToShow);
      setSidePanelOpen(true);
      onSidePanelStateChange?.(true, 'library');
    }
  };

  // Keep sidePanelSong in sync with updated like counts and titles
  useEffect(() => {
    if (sidePanelSong) {
      const updatedSongs = getLibrarySongsWithCounts();
      const updatedSong = updatedSongs.find(song => song.id === sidePanelSong.id);
      if (updatedSong && (updatedSong.likeCount !== sidePanelSong.likeCount || updatedSong.title !== sidePanelSong.title)) {
        setSidePanelSong(updatedSong);
      }
    }
  }, [songLikeCounts, songTitles, sidePanelSong?.id]);

  // Handle scroll for gradient effect and lazy loading
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const scrollTop = contentRef.current.scrollTop;
        const scrollHeight = contentRef.current.scrollHeight;
        const clientHeight = contentRef.current.clientHeight;
        
        // Start fading in after 20px scroll, fully visible at 100px
        const opacity = Math.min(scrollTop / 100, 1);
        setScrollOpacity(opacity);
        
        // Check if we're near the bottom (within 200px)
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 200;
        
        if (isNearBottom && hasMoreSongs && !isLoadingMore) {
          loadMoreSongs();
        }
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, [hasMoreSongs, isLoadingMore, loadMoreSongs]);

  // Reset lazy loading when filters change
  useEffect(() => {
    setLoadedSongsCount(30);
    setIsLoadingMore(false);
    setHasMoreSongs(true);
  }, [activeFilters, searchValue, selectedStyles, currentSort]);

  return (
    <div className="flex flex-col h-screen bg-transparent flex-1 min-w-max xl:min-w-[450px] relative overflow-hidden">
      {/* Content Area - Scrollable (behind headers) */}
      <div 
        ref={contentRef}
        className="absolute inset-0 overflow-y-auto"
        style={{
          right: sidePanelOpen ? '274px' : '0' // Make room for side panel (250px + 24px margin)
        }}
      >
        <div 
          className="px-4"
          style={{ 
            paddingTop: '193px', // Increased spacing to account for header + tabs + filter nav
            paddingBottom: hasPlaybar ? '100px' : '20px' 
          }}
        >
          {/* Songs List */}
          {activeTab === 'songs' && (
            <WorkspaceList 
              onCellAction={handleCellAction}
              isCreating={false}
              isCompact={currentView === 'compact'}
              newSongs={[]}
              activeFilters={activeFilters}
              likedSongs={likedSongs}
              dislikedSongs={dislikedSongs}
              commentedSongs={commentedSongs}
              publishedSongs={publishedSongs}
              selectedSongs={selectedSongs}
              deletedSongs={deletedSongs}
              playedSongs={playedSongs}
              searchValue={searchValue}
              multiSelectMode={selectedSongs.size > 0}
              onPlaySong={onPlaySong}
              onPauseSong={onPauseSong}
              currentlyPlayingSong={currentlyPlayingSong}
              onMarkAsPlayed={(songId) => setPlayedSongs(prev => new Set([...prev, songId]))}
              isLibrary={true}
              onRowClick={handleRowClick}
              songs={getLibrarySongsWithCounts()}
              selectedStyles={selectedStyles}
              isLoadingMore={isLoadingMore}
              hasMoreSongs={hasMoreSongs}
            />
          )}

          {/* Placeholder for other tabs */}
          {activeTab === 'playlists' && (
            <div className="flex items-center justify-center w-full h-64">
              <p className="text-[#726e6c]">Playlists coming soon...</p>
            </div>
          )}

          {activeTab === 'workspaces' && (
            <div className="flex items-center justify-center w-full h-64">
              <p className="text-[#726e6c]">Workspaces coming soon...</p>
            </div>
          )}

          {activeTab === 'personas' && (
            <div className="flex items-center justify-center w-full h-64">
              <p className="text-[#726e6c]">Personas coming soon...</p>
            </div>
          )}

          {activeTab === 'hooks' && (
            <div className="flex items-center justify-center w-full h-64">
              <p className="text-[#726e6c]">Hooks coming soon...</p>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="flex items-center justify-center w-full h-64">
              <p className="text-[#726e6c]">History coming soon...</p>
            </div>
          )}

          {activeTab === 'archive' && (
            <div className="flex items-center justify-center w-full h-64">
              <p className="text-[#726e6c]">Archive coming soon...</p>
            </div>
          )}
        </div>
      </div>

      {/* Header Background - Opaque background that blends with gradient */}
      <div 
        className="absolute top-0 left-0 right-0 z-15 pointer-events-none"
        style={{
          height: '192px'
        }}
      >
        <div
          style={{
            marginRight: sidePanelOpen ? '274px' : '0',
            background: `linear-gradient(180deg, ${colors.bg.primary} 0%, ${colors.bg.primary} 75%, ${colors.bg.primary}F2 100%)`,
            height: '100%'
          }}
        />
      </div>

      {/* Header - Fixed position */}
      <div className="absolute top-0 left-0 right-0 z-20 pointer-events-auto">
        {/* Header Content Wrapper - constrains width but keeps Y position */}
        <div 
          style={{ 
            marginRight: sidePanelOpen ? '274px' : '0'
          }}
        >
          
        </div>
      </div>

      {/* Sticky Header Container - Library Header + Tabs + FilterNav */}
      <div 
        className="sticky top-0 z-20 bg-transparent pointer-events-auto"
        style={{
          left: '0px',
          right: sidePanelOpen ? '274px' : '0px'
        }}
      >
        {/* Library Header */}
        <div 
          style={{
            paddingTop: '16px',
            paddingLeft: '16px',
            paddingRight: sidePanelOpen ? '16px' : '16px',
          }}
        >
          <div className="flex justify-between items-center w-full h-full px-0">
            <h1 className="text-white" style={{ fontSize: '28px', fontWeight: 600, lineHeight: '36px' }}>Library</h1>
            <div className="flex items-center">
              <MediumButton
                variant="secondary"
                text="Audio"
                leftIcon={<PlusIcon />}
                onClick={handleAudioClick}
              />
            </div>
          </div>
        </div>

        {/* Library Tabs */}
        <div className="px-4 pt-4">
          <div className="flex items-center w-full border-b border-white/20">
            {tabs.map((tab) => (
              <LibraryTab
                key={tab.id}
                label={tab.label}
                isActive={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              />
            ))}
          </div>
        </div>

        {/* Filter Navigation */}
        <div 
          className="w-full pt-3"
          style={{
            paddingRight: sidePanelOpen ? '32px' : '16px',
            paddingLeft: '16px'
          }}
        >
          <FilterNav
            activeFilters={activeFilters}
            searchValue={searchValue}
            filterCount={filterCount}
            currentSort={currentSort}
            onFilterClick={handleFilterClick}
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
            sidePanelOpen={sidePanelOpen}
            availableStyles={getAvailableStyles()}
            selectedStyles={selectedStyles}
            onStyleToggle={handleStyleToggle}
            styleCounts={getStyleCounts()}
            currentView={currentView}
            onViewChange={handleViewChange}
          />
        </div>
      </div>

      {/* Gradient Overlay - creates soft transition from header to content */}
      <div 
        className="absolute top-0 left-0 right-0 z-10 pointer-events-none"
        style={{
          opacity: scrollOpacity,
          height: '252px'
        }}
      >
        <div
          style={{
            marginRight: sidePanelOpen ? '274px' : '0',
            background: `linear-gradient(180deg, ${colors.bg.primary} 0%, ${colors.bg.primary} 40%, ${colors.bg.primary}F2 60%, ${colors.bg.primary}D9 75%, ${colors.bg.primary}99 90%, ${colors.bg.primary}00 100%)`,
            height: '100%'
          }}
        />
      </div>

      {/* MultiSelect component - anchored to bottom, centered within library */}
      <div 
        className="absolute left-4 z-50 flex justify-center pointer-events-none"
        style={{ 
          bottom: hasPlaybar ? '108px' : '24px',
          right: sidePanelOpen ? '290px' : '16px'
        }}
      >
        <div className="pointer-events-auto">
          <MultiSelect
            selectedCount={selectedSongs.size}
            onClear={handleMultiSelectClear}
            onDownload={handleMultiSelectDownload}
            onNewWorkspace={handleMultiSelectNewWorkspace}
            onAddToPlaylist={handleMultiSelectAddToPlaylist}
            onDelete={handleMultiSelectDelete}
          />
        </div>
      </div>

      {/* Side Panel */}
      <SidePanel
        isOpen={sidePanelOpen}
        onClose={handleSidePanelClose}
        song={sidePanelSong}
        isWorkspace={false}
        isLiked={sidePanelSong ? likedSongs.has(sidePanelSong.id) : false}
        onLike={handleSidePanelLike}
        hasPlaybar={hasPlaybar}
        onTitleEdit={handleTitleEdit}
      />
    </div>
  );
};

export default Library;